/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Foundational style overrides
import colors from '@salesforce/retail-react-app/app/theme/foundations/colors'
import gradients from '@salesforce/retail-react-app/app/theme/foundations/gradients'
import shadows from '@salesforce/retail-react-app/app/theme/foundations/shadows'
import sizes from '@salesforce/retail-react-app/app/theme/foundations/sizes'
import space from '@salesforce/retail-react-app/app/theme/foundations/space'
import breakpoints from './foundations/breakpoints'
import layerStyles from './foundations/layerStyles'
import styles from './foundations/styles'

// Base component style overrides
import Accordion from '@salesforce/retail-react-app/app/theme/components/base/accordion'
import Alert from '@salesforce/retail-react-app/app/theme/components/base/alert'
import Badge from '@salesforce/retail-react-app/app/theme/components/base/badge'
import Checkbox from '@salesforce/retail-react-app/app/theme/components/base/checkbox'
import FormLabel from '@salesforce/retail-react-app/app/theme/components/base/formLabel'
import Icon from '@salesforce/retail-react-app/app/theme/components/base/icon'
import Input from '@salesforce/retail-react-app/app/theme/components/base/input'
import Radio from '@salesforce/retail-react-app/app/theme/components/base/radio'
import Select from '@salesforce/retail-react-app/app/theme/components/base/select'
import Skeleton from '@salesforce/retail-react-app/app/theme/components/base/skeleton'
import Tooltip from '@salesforce/retail-react-app/app/theme/components/base/tooltip'
import Button from './components/base/button'
import Container from './components/base/container'
import Drawer from './components/base/drawer'
import Heading from './components/base/heading'
import Modal from './components/base/modal'
import Popover from './components/base/popover'
// Project Component style overrides
import App from '@salesforce/retail-react-app/app/theme/components/project/_app'
import Breadcrumb from '@salesforce/retail-react-app/app/theme/components/project/breadcrumb'
import CheckoutFooter from '@salesforce/retail-react-app/app/theme/components/project/checkout-footer'
import LocaleSelector from '@salesforce/retail-react-app/app/theme/components/project/locale-selector'
import NestedAccordion from '@salesforce/retail-react-app/app/theme/components/project/nested-accordion'
import OfflineBanner from '@salesforce/retail-react-app/app/theme/components/project/offline-banner'
import SocialIcons from '@salesforce/retail-react-app/app/theme/components/project/social-icons'
import AddToCartButton from './components/project/add-to-cart-button'
import EventsContentGrid from './components/project/events-content-grid'
import HomeServiceCard from './components/project/home-service-card'
import HomeServiceSection from './components/project/home-service-section'
import ImageGallery from './components/project/image-gallery'
import Pagination from './components/project/pagination'
import ProductTile from './components/project/product-tile'

import Account from './components/project/account'
import Banner from './components/project/banner'
import BlogCard from './components/project/blog-card'
import BlogDetailRichText from './components/project/blog-detail-rich-text'
import BookingCard from './components/project/booking-card'
import BookingSection from './components/project/booking-section'
import ButtonType from './components/project/button-type'
import CategoryCard from './components/project/category-card'
import ContactPage from './components/project/contact-page'
import DrawerMenu from './components/project/drawer-menu'
import Footer from './components/project/footer'
import FooterAccordion from './components/project/footer-accordion'
import Header from './components/project/header'
import HeroSection from './components/project/hero-section'
import HomepageBlogsSection from './components/project/homepage-blogs-section'
import LinkType from './components/project/link-type'
import LinksList from './components/project/links-list'
import ListMenu from './components/project/list-menu'
import LocationPage from './components/project/location-page'
import MegaLink from './components/project/mega-link'
import NewsletterModal from './components/project/newsletter-modal'
import Overlay from './components/project/overlay'
import ProductScroller from './components/project/product-scroller'
import ProductScrollerTile from './components/project/product-tile-carousel'
import Search from './components/project/search'
import SearchProductTile from './components/project/search-product-tile'
import SliderProgress from './components/project/slider-progress'
import StaticTemplatePage from './components/project/static-template-page'
import SwatchGroup from './components/project/swatch-group'
import VideoSection from './components/project/video-section'
// Please refer to the Chakra-Ui theme customization docs found
// here https://chakra-ui.com/docs/theming/customize-theme to learn
// more about extending and overriding themes for your project.
export const overrides = {
    styles,
    layerStyles,
    colors,
    breakpoints,
    sizes,
    space,
    gradients,
    shadows,
    components: {
        // base components
        Accordion,
        Alert,
        Badge,
        Button,
        Checkbox,
        Container,
        Drawer,
        FormLabel,
        Icon,
        Input,
        Modal,
        Popover,
        Radio,
        Select,
        Skeleton,
        Tooltip,
        Heading,
        // project components
        App,
        Breadcrumb,
        Header,
        Search,
        Overlay,
        Footer,
        VideoSection,
        FooterAccordion,
        CheckoutFooter,
        HeroSection,
        CategoryCard,
        LinksList,
        MegaLink,
        ListMenu,
        DrawerMenu,
        NestedAccordion,
        LocaleSelector,
        OfflineBanner,
        SocialIcons,
        Pagination,
        ProductTile,
        SwatchGroup,
        ImageGallery,
        ProductScrollerTile,
        ProductScroller,
        HomeServiceCard,
        HomeServiceSection,
        AddToCartButton,
        BookingSection,
        BookingCard,
        HomepageBlogsSection,
        BlogCard,
        ButtonType,
        LinkType,
        NewsletterModal,
        SearchProductTile,
        StaticTemplatePage,
        ContactPage,
        LocationPage,
        SliderProgress,
        Account,
        Banner,
        BlogDetailRichText,
        EventsContentGrid
    }
}

export default extendTheme(overrides)
